<template>
    <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-position="left"
        require-asterisk-position="left"
        label-width="auto"
        class="mt-28"
    >
        <el-form-item :label="account.includes('@') ? '邮箱' : '手机号'">
            <div>{{ account }}</div>
        </el-form-item>
        <el-form-item prop="captcha">
            <div class="phone-code">
                <el-input
                    class="phone-code-input"
                    size="large"
                    placeholder="验证码"
                    v-model="form.captcha"
                    maxlength="10"
                    @keyup.enter="next"
                >
                    <template #prefix><i class="iconfont icon-code"></i></template>
                </el-input>
                <div class="code-position">
                    <span
                        id="captcha-button"
                        v-if="countDown < 1"
                        class="code-btn"
                        v-loading="loading"
                        @click.stop="onClickCheckPhone"
                    >
                        发送验证码
                    </span>
                    <span v-else class="count-down">{{ countDown }}秒重新获取</span>
                </div>
            </div>
        </el-form-item>
    </el-form>
    <div v-show="showCaptchaWrap" class="captcha-wrap">
        <div id="captcha-element" @click.stop="() => {}" />
    </div>
    <div class="btn-box">
        <el-button size="large" @click="emits('cancel')">取消</el-button>
        <el-button size="large" type="primary" @click="next" :loading="btnLoading" :disabled="isSubmitDisabled"
            >立即验证</el-button
        >
    </div>
</template>

<script setup lang="ts">
import { ElButton, ElInput, ElForm, ElFormItem, FormInstance } from "element-plus";
import { messageService } from "@/api/services/messageService";
import { loginService } from "@/api/services/loginService";
import type { CaptchaLoginForm } from "@supplier/entity";
import moment from "moment";
import { type CaptchaType, useCaptcha } from "@/views/pages/login/hooks";
import { getDeviceCode } from "@/utils/deviceCode";
import { computed, ref, Ref, onMounted } from "vue";
import { ElMessage } from "element-plus";
import { SYSTEM_LOGIN_TYPE } from "@/views/pages/sassLogin/type.ts";
import { customerMessageService } from "@/api/services/customerMessageService.ts";
import { customerLoginService } from "@/api/services/customerLoginService.ts";
import { ValidatePhone } from "@/entity/login.ts";

const props = defineProps<{
    account: string;
    systemType: number;
}>();
const emits = defineEmits(["cancel", "resetPassword", "getToken"]);

const formRef = ref<FormInstance>();
const form = ref({
    captcha: "",
    deviceCode: ""
});
const btnLoading = ref<boolean>(false);
const isSubmitDisabled = computed(() => {
    return !form.value.captcha;
});

const wait = ref<boolean>(false);
// 验证规则
const rules = ref({
    captcha: [{ required: true, message: "请输入验证码", trigger: "blur" }]
});
// 验证码
const randomNum = ref("");
const loadingCheck = ref(true);
const captchaVerifyCallback = async (captchaVerifyParam: string) => {
    loadingCheck.value = true;
    randomNum.value = `${moment(new Date()).valueOf()}${Math.random() * 1000000000}`;
    const params = {
        serialNumber: randomNum.value,
        SceneId: "1wjppc2z",
        captchaVerifyParam
    };
    try {
        if (props.systemType === SYSTEM_LOGIN_TYPE.ADMIN) {
            await messageService.validateCaptcha(params);
        } else if (props.systemType === SYSTEM_LOGIN_TYPE.CUSTOMER) {
            await customerMessageService.validateCaptcha(params);
        }
        loadingCheck.value = false;
        await onClickSendCode();
    } catch (e) {
        console.error(e);
        loadingCheck.value = false;
        captcha.value!.refresh();
    }
};

/** 验证码实例 */
let captcha: Ref<CaptchaType | undefined>;
captcha = useCaptcha({ captchaVerifyCallback });
const showCaptchaWrap = ref(false);
const onClickCheckPhone = async () => {
    if (!props.account) return;
    captcha.value!.refresh();
    showCaptchaWrap.value = true;
};

const countDown = ref(0);
const loading = ref(false);
const onClickSendCode = async () => {
    try {
        loading.value = true;
        if (props.systemType === SYSTEM_LOGIN_TYPE.ADMIN) {
            await messageService.sendPhoneCode(props.account);
        } else if (props.systemType === SYSTEM_LOGIN_TYPE.CUSTOMER) {
            await customerMessageService.sendPhoneCode(props.account, randomNum.value);
        }
        closeCaptchaWrap();
        loading.value = false;
        countDown.value = 60;
        setCodeCountDown();
        wait.value = true;
    } catch (e) {
        console.error(e);
        captcha.value!.refresh();
    }
};
const setCodeCountDown = (): void => {
    const timer = setInterval(() => {
        countDown.value -= 1;
        if (countDown.value === 0) {
            clearInterval(timer);
        }
    }, 1000);
};

// 下一步
const next = () => {
    formRef.value!.validate(async valid => {
        if (valid) {
            try {
                btnLoading.value = true;
                let data = "";
                if (props.systemType === SYSTEM_LOGIN_TYPE.ADMIN) {
                    data = await adminLogin();
                } else if (props.systemType === SYSTEM_LOGIN_TYPE.CUSTOMER) {
                    data = await customerLogin();
                }
                btnLoading.value = false;
                if (!data) {
                    ElMessage.error("登录失败，请重新登录！");
                    return;
                }
                emits("getToken", data);
            } catch (e) {
                console.error(e);
                btnLoading.value = false;
            }
        }
    });
};
const adminLogin = async () => {
    // 新设备登录失败 再次短信验证码登录
    let params: ValidatePhone = {
        phone: props.account as string,
        code: form.value.captcha,
        deviceCode: form.value.deviceCode
    };
    const { accessToken } = await loginService.loginPhone(params);
    return accessToken;
};
const customerLogin = async () => {
    let params: CaptchaLoginForm = {
        mobile: props.account,
        captcha: form.value.captcha,
        deviceCode: form.value.deviceCode
    };
    const { accessToken } = await customerLoginService.login(params);
    return accessToken;
};
const closeCaptchaWrap = () => {
    showCaptchaWrap.value = false;
};

onMounted(() => {
    setTimeout(async () => {
        // 获取设备UUID
        await getDeviceCode((deviceCode: string) => {
            form.value.deviceCode = deviceCode;
        });
    }, 100);
});
</script>
<style scoped lang="scss">
$primary-color: #1c5bff;
.btn-box {
    width: 100%;
    display: flex;
    .el-button {
        flex: 1;
    }
}
:deep() {
    .phone-code {
        position: relative;
        width: 100%;

        &-input {
            width: 100%;

            .el-input__inner {
                padding-right: 80px;
            }
        }

        .code-position {
            position: absolute;
            top: 4px;
            right: 0;
            width: fit-content;

            .code-btn {
                cursor: pointer;
                color: $primary-color;
                text-align: center;
                padding: 6px 16px;
                border-radius: 6px;
                font-size: 12px;
                font-style: normal;
                font-weight: 500;
                line-height: 14px;
            }

            .count-down {
                position: relative;
                right: 16px;
                color: #cccccc;
            }
        }
    }
}
.title {
    .tips {
        padding-top: 8px;
        color: #00000066;
        font-size: 12px;
        font-weight: 400;
        line-height: 12px;
    }
}

.captcha-wrap {
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
}
</style>
